/**
 * Soft I1b: screen-only review breakdown under the share card.
 *
 *   setup meta line   `6명 · 0.5/1 · 앤티 0.1 · 유효 100bb`
 *   per street        `플롭 Q♦7♣2♥ · 팟 7.5bb · 2명`
 *                     `MP 체크 · BTN 벳 3bb · MP 콜`
 *
 * Reads existing calculations only: the single line generator
 * (handLineSegments — same action / board text as the card line),
 * deriveHandFlow (players still in at the start of a street) and inputPot
 * (pot at the start of a street, 「?」 when any part is unknown or the
 * entered setup pot disagrees). Streets never reached render nothing.
 * Entered facts only — no %, odds, SPR or evaluation. Never imported by
 * Copy / PNG / `#/h/` (shareCopy, imageExport, handLink).
 */
import { t } from '../i18n/strings'
import { deriveHandFlow, formatBb } from '../lib/handFlow'
import { formatBlindsNotation, LINE_SEPARATOR } from '../lib/lineText'
import { formatSeatCode, handLineSegments } from '../lib/poker'
import type { HandState, Street } from '../lib/types'
import { el } from './dom'
import { inputPot } from './inputPot'
import { classifyVocab, type VocabResult } from './vocabTags'
import { dotSegments } from './dotSegments'

const STREETS: Street[] = ['preflop', 'flop', 'turn', 'river']

export type ReviewGroup = { street: Street; header: string; actions: string }

function num(n: number): string {
  return String(Math.round(n * 100) / 100)
}

/** Setup meta: players · blinds · ante · stack (bb). */
export function reviewMetaText(hand: HandState): string {
  const ui = t()
  const blinds = formatBlindsNotation({ smallBlind: hand.smallBlind, bigBlind: hand.bigBlind, ante: undefined })
  const bits = [ui.playersUnit(hand.playerCount), blinds]
  if (hand.ante != null && Number.isFinite(hand.ante) && hand.ante > 0) bits.push(ui.reviewAnte(num(hand.ante)))
  if (hand.effectiveStackBb != null && Number.isFinite(hand.effectiveStackBb) && hand.effectiveStackBb > 0) {
    bits.push(ui.reviewEffStack(formatBb(hand.effectiveStackBb)))
  } else if (Number.isFinite(hand.heroStackBb) && hand.heroStackBb > 0) {
    // No effective stack: label the hero's own stack with the hero seat (`BTN 100bb`).
    bits.push(`${formatSeatCode(hand.heroPosition, hand.playerCount)} ${formatBb(hand.heroStackBb)}`)
  }
  return bits.join(LINE_SEPARATOR)
}

/** Pot at the start of `street` (bb) or null (= 「?」). */
function potAtStart(hand: HandState, street: Street): number | null {
  // Entered setup pot that disagrees with the entered actions → every header 「?」.
  const setupPot = hand.potBb != null && Number.isFinite(hand.potBb) && hand.potBb > 0
  if (setupPot && inputPot(hand).kind === 'unknown' && inputPot({ ...hand, potBb: undefined }).kind === 'known') return null
  const idx = STREETS.indexOf(street)
  const before = hand.actions.filter((a) => STREETS.indexOf(a.street) < idx)
  const p = inputPot({ ...hand, actions: before, potBb: undefined })
  return p.kind === 'known' ? p.bb : null
}

/** Seats not folded at the start of `street` (all-ins count); null = unknowable. */
function playersAtStart(hand: HandState, street: Street): number | null {
  const idx = STREETS.indexOf(street)
  const before = hand.actions.filter((a) => STREETS.indexOf(a.street) < idx)
  // Preflop = every seat dealt in (leading folds still count there); later
  // streets = seats not folded before it (all-ins count).
  const live = deriveHandFlow({ ...hand, actions: before }).live
  return live.length > 0 ? live.length : null
}

export function reviewGroups(hand: HandState): ReviewGroup[] {
  const ui = t()
  const segs = handLineSegments(hand)
  const out: ReviewGroup[] = []
  for (const street of STREETS) {
    const board = segs.find((s) => s.kind === 'board' && s.street === street)
    const acts = segs.filter((s) => s.kind === 'action' && s.street === street)
    if (street !== 'preflop' && !board && !acts.length) continue
    const live = playersAtStart(hand, street)
    const pot = potAtStart(hand, street)
    const header = [
      board ? board.text : ui.street[street],
      ui.tableVizPotValue(pot == null ? '?' : formatBb(pot)),
      live == null ? '' : ui.reviewPlayers(live),
    ].filter(Boolean).join(LINE_SEPARATOR)
    out.push({ street, header, actions: acts.map((s) => s.text).join(LINE_SEPARATOR) })
  }
  return out
}

/** Poker vocabulary tag (screen only): quiet text, no chip / icon / tooltip. */
function vocabSpan(label: string): HTMLElement {
  return el('span', { className: 'vocabTag', text: label })
}

/** Street header with the board tag right after the board cards. */
function headerNodes(g: ReviewGroup, vocab: VocabResult): Array<string | HTMLElement> {
  const tag = g.street === 'preflop' ? undefined : vocab.board[g.street]
  if (!tag) return [g.header]
  const label = t().reviewVocab[tag]
  const i = g.header.indexOf(LINE_SEPARATOR)
  const head = i < 0 ? g.header : g.header.slice(0, i)
  const tail = i < 0 ? '' : g.header.slice(i)
  return [`${head} `, vocabSpan(label), tail]
}

/** Action line: a tagged action shows the tag in place of its verb. */
function actionNodes(hand: HandState, street: Street, vocab: VocabResult): Array<string | HTMLElement> {
  const ui = t()
  const segs = handLineSegments(hand).filter((s) => s.kind === 'action' && s.street === street)
  const out: Array<string | HTMLElement> = []
  segs.forEach((s, i) => {
    if (i > 0) out.push(LINE_SEPARATOR)
    if (s.kind !== 'action') return
    const tag = vocab.actions[s.actionId]
    const a = hand.actions.find((x) => x.id === s.actionId)
    const verb = a ? ui.actionType[a.type] : undefined
    const at = verb ? s.text.indexOf(` ${verb}`) : -1
    if (!tag || at < 0 || !verb) { out.push(s.text); return }
    out.push(s.text.slice(0, at + 1), vocabSpan(ui.reviewVocab[tag]), s.text.slice(at + 1 + verb.length))
  })
  return out
}

export function renderReviewBreakdown(hand: HandState): HTMLElement {
  const ui = t()
  const vocab = classifyVocab(hand)
  return el('section', { className: 'reviewBreakdown', attrs: { 'aria-label': ui.reviewBreakdownAria, 'data-soft-i1b': 'review' } }, [
    el('p', { className: 'reviewMeta' }, dotSegments([reviewMetaText(hand)], LINE_SEPARATOR)),
    ...reviewGroups(hand).map((g) =>
      el('div', { className: 'reviewStreet', attrs: { 'data-street': g.street } }, [
        el('p', { className: 'reviewStreetHead' }, dotSegments(headerNodes(g, vocab), LINE_SEPARATOR)),
        g.actions ? el('p', { className: 'reviewStreetActions' }, dotSegments(actionNodes(hand, g.street, vocab), LINE_SEPARATOR)) : null,
      ]),
    ),
  ])
}
